/**
 * L4-KILL-004: KillNotificationStack Container Component
 *
 * Container that renders kill notifications stacked vertically.
 * Subscribes to KillNotificationManager for updates.
 *
 * @see docs/specs/L4-TASKS/kill-notification/L4-KILL-004-kill-notification-stack.md
 */

import React, { useState, useEffect } from 'react';
import { KillNotification } from './KillNotification';
import type { KillNotification as KillNotificationData } from '../../services/kill-notification-manager';
import type { KillNotificationManager } from '../../services/kill-notification-manager';

export interface KillNotificationStackProps {
  manager: KillNotificationManager;
}

/**
 * Container that renders kill notifications stacked vertically
 * Subscribes to KillNotificationManager for updates
 */
export const KillNotificationStack: React.FC<KillNotificationStackProps> = ({
  manager
}) => {
  const [notifications, setNotifications] = useState<KillNotificationData[]>([]);

  useEffect(() => {
    // Subscribe to manager updates
    const unsubscribe = manager.subscribe(setNotifications);

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [manager]);

  // Filter out hidden notifications (for cleanup)
  const visibleNotifications = notifications.filter(
    n => n.fadeState !== 'hidden'
  );

  // Don't render container if no notifications
  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        width: 250,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
        zIndex: 100, // Same as ZoomControls and CombatLog
      }}
      aria-label="Kill notifications"
      data-testid="kill-notification-stack"
    >
      {visibleNotifications.map((notification) => (
        <KillNotification
          key={notification.id}
          notification={notification}
        />
      ))}
    </div>
  );
};

export default KillNotificationStack;
